import { BingoLoadGameResponse } from '../../types/bingo-events/in/backend/load-game-response';

import { account } from './account';
import { calls } from './calls';
import { error } from './error';
import { fireBallStatus, resetFireBallStatus } from './fireball';
import { current, DEFAULT_GAME_STATE, next } from './game';
import { meta } from './meta';
import { resetPickedCards } from './picked-cards';
import { screenConfig } from './screen-config';
import { createTimeRemainingLoop } from './timer';
import { resetWinners } from './winners';
import { compatibleCalls } from '../../socket/backend/session';
import { isSuspended, suspendApp } from '../../lib/suspension';

export const initialize = (
    response: BingoLoadGameResponse,
    attemptAutoBuy: () => void
) => {
    if (isSuspended()) return;
    if (response.success) {
        const {
            data: {
                current_calls,
                wagered,
                player_cash,
                player_bonus,
                player_fp_balance,
                ...data
            },
            nextGame,
        } = response;

        const newCurrent = {
            ...DEFAULT_GAME_STATE,
            ...data,
        };

        if (nextGame) {
            resetFireBallStatus();
            next.set(newCurrent);

            meta.enabled.buying.set(true);

            clearInterval(meta.timer.id.value!);

            if (newCurrent.game_mode_time_remaining) {
                createTimeRemainingLoop(newCurrent.game_mode_time_remaining);
            }

            attemptAutoBuy();
            resetPickedCards();
        } else {
            let currentCallNumbers = (current_calls || []).map(Number);
            const sameSession = current.game_sid.value === newCurrent.game_sid;
            if (
                sameSession &&
                !compatibleCalls(calls.value, currentCallNumbers)
            ) {
                suspendApp({
                    event: 'bingo:loadGameResp:conflicting-calls',
                    expectedSessionId: current.game_sid.value,
                    receivedSessionId: newCurrent.game_sid,
                });
                return;
            }
            if (sameSession && calls.value.length > currentCallNumbers.length)
                currentCallNumbers = [...calls.value];
            if (!sameSession) resetWinners();
            const lastCurrentCall =
                currentCallNumbers[currentCallNumbers.length - 1];
            const hasCalledFireBall =
                !!newCurrent.fireball &&
                currentCallNumbers.includes(newCurrent.fireball);

            if (hasCalledFireBall) {
                fireBallStatus.set(
                    lastCurrentCall === newCurrent.fireball
                        ? 'pending'
                        : 'snuffed'
                );
            } else {
                resetFireBallStatus();
            }

            current.set({
                ...newCurrent,
                current_part: sameSession
                    ? Math.max(
                          current.current_part.value,
                          newCurrent.current_part || 0
                      )
                    : newCurrent.current_part || 0,
            });

            calls.set(currentCallNumbers);

            if (!meta.currency.value) {
                meta.currency.set(
                    screenConfig.screenConfig.display.defaultCurrency.value ===
                        'player'
                        ? data.player_currency
                        : data.game_currency
                );
            }
        }

        account.set({
            wagered,
            player_cash,
            player_bonus,
            player_fp_balance: player_fp_balance || 0,
        });
    } else {
        error.set({
            type: response.msg,
        });
        meta.modals.msgCommon.set({
            name: 'error.game.running',
            isOpen: true,
            closeBehavior: 'close',
        });
    }
};
